/**
 * EmojiPicker — reusable categorized emoji picker with keyword search.
 * Used in comments, chat input, and post creation.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import {
  addRecentEmoji,
  EMOJI_CATEGORIES,
  filterEmojiCategories,
  readRecentEmojis,
} from './emojiData';
import { ClockIcon, SearchIcon } from './icons';
import './emojiPicker.css';

const RECENT_EMOJIS_STORAGE_KEY = 'wlb.recent-emojis';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [recentEmojis, setRecentEmojis] = useState<string[]>(() => {
    try {
      return readRecentEmojis(window.localStorage.getItem(RECENT_EMOJIS_STORAGE_KEY));
    } catch {
      return [];
    }
  });
  const [activeCategory, setActiveCategory] = useState('recent');

  const pickerCategories = useMemo(() => [
    {
      id: 'recent',
      label: 'Recently used',
      labelKey: 'emoji.categories.recent',
      emojis: recentEmojis,
    },
    ...EMOJI_CATEGORIES,
  ], [recentEmojis]);

  const filteredCategories = useMemo(
    () => search.trim() ? filterEmojiCategories(search) : pickerCategories,
    [pickerCategories, search],
  );

  const selectEmoji = (emoji: string) => {
    setRecentEmojis(current => {
      const next = addRecentEmoji(current, emoji);
      try {
        window.localStorage.setItem(
          RECENT_EMOJIS_STORAGE_KEY,
          JSON.stringify(next),
        );
      } catch {
        // Restricted storage must not prevent emoji selection.
      }
      return next;
    });
    onSelect(emoji);
  };

  return (
    <div className="emoji-picker" role="group" aria-label={t('comments.emojiPicker')}>
      <div className="emoji-picker__search-bar">
        <span className="emoji-picker__search-icon" aria-hidden="true">
          <SearchIcon size={18} />
        </span>
        <input
          className="emoji-picker__search"
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('comments.searchEmojiPlaceholder')}
          aria-label={t('comments.searchEmojiPlaceholder')}
        />
      </div>

      {!search.trim() && (
        <div className="emoji-picker__tabs" role="tablist">
          {pickerCategories.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`emoji-picker__tab${activeCategory === cat.id ? ' emoji-picker__tab--active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              role="tab"
              aria-selected={activeCategory === cat.id}
              aria-label={t(cat.labelKey)}
              title={t(cat.labelKey)}
            >
              {cat.id === 'recent'
                ? <ClockIcon size={20} />
                : cat.emojis[0]}
            </button>
          ))}
        </div>
      )}

      <div className="emoji-picker__body">
        {filteredCategories.length === 0 ? (
          <p className="emoji-picker__empty">{t('comments.searchEmojiEmpty')}</p>
        ) : (
          filteredCategories.map(cat => (
            <section
              key={cat.id}
              className="emoji-picker__section"
              id={`emoji-section-${cat.id}`}
              hidden={!search.trim() && activeCategory !== cat.id}
            >
              <h4 className="emoji-picker__section-label">{t(cat.labelKey)}</h4>
              {cat.id === 'recent' && cat.emojis.length === 0 ? (
                <p className="emoji-picker__empty">
                  {t('emoji.noRecent')}
                </p>
              ) : (
                <div className="emoji-picker__grid">
                  {cat.emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-picker__item"
                    onClick={() => selectEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </div>
    </div>
  );
}
